import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { Nav } from '../components/Nav';
import { Footer } from '../components/Footer';
import { Frame } from '../components/Frame';
import { VideoEmbed } from '../components/VideoEmbed';
import { AboutBlueprint } from '../components/AboutBlueprint';
import { aboutFacts, aboutStory, aboutTimeline, aboutWhy } from '../data/about';
type AboutProps = {
  accent: 'violet' | 'signal';
};
const PORTRAIT_URL = 'https://cdn.magicpatterns.com/uploads/aqvUUHV2E7Uhyq1rY99QtV/9ef6dca0-2ce2-48e9-9a4c-d9ce7642602a.jpg';
const TEAM_IMAGE_URL = 'https://cdn.magicpatterns.com/uploads/2C32J2PgLWNZUAeXYXFC2W/27db10_ee4a0a0841944317b00b96a9e1ed32cb~mv2.avif';
export function About({
  accent
}: AboutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Über mich · Konrad Walz';
  }, []);
  return <div lang="de" data-accent={accent} className="min-h-screen w-full bg-white font-sans text-ink antialiased" data-id="element-603" data-mp="1luu793ihu4bk">
      <Nav data-id="element-604" data-mp="1uibkkdb5d5mq" />
      <main data-id="element-605" data-mp="1ujw6xfnb5i9i">
        <Frame data-id="element-606" data-mp="1ujmocd4leddu">
          <div className="relative overflow-hidden px-5 pb-20 pt-12 md:px-6 md:pb-28 md:pt-20" data-id="element-607" data-mp="1piyqp8luyyan">
            <AboutBlueprint data-id="element-608" data-mp="23i87oahw1wfo" />
            <div className="relative" data-id="element-609" data-mp="23jj7jz87qk1i">
            <Link to="/" className="inline-flex items-center gap-1.5 text-[14px] text-muted transition-colors duration-150 hover:text-ink" data-id="element-610" data-mp="23gdl6rqyxw9h">
              <ArrowLeftIcon className="h-4 w-4" data-id="element-611" data-mp="2df33umqovkqs" />
              Zur Startseite
            </Link>

            {/* Intro + portrait */}
            <section className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-20" data-id="element-612" data-mp="2m8y87sjzl75d">
              <div data-id="element-613" data-mp="2i7cobn91bslo">
                <h1 className="font-display text-[40px] font-semibold leading-[1.03] tracking-[-0.04em] md:text-[60px]" data-id="element-614" data-mp="2i6ulg7rvag4s">
                  Konrad Walz.
                </h1>
                <p className="mt-4 text-[19px] font-medium tracking-[-0.01em] text-ink md:text-[21px]" data-id="element-615" data-mp="2w4sztd66ucka">
                  Technischer Produktdesigner &amp; externer Konstrukteur
                </p>
                <div className="mt-8 max-w-[640px] space-y-5 text-[17px] leading-relaxed text-ink/75 md:text-[18px]" data-id="element-616" data-mp="1ostj2ffv72e4">
                  {aboutStory.map(p => <p key={p} data-id="element-617" data-mp="1xtrpt5hoto0e">{p}</p>)}
                </div>
                <dl className="mt-10 grid max-w-[640px] gap-x-8 gap-y-5 border-t border-line pt-8 sm:grid-cols-2" data-id="element-618" data-mp="22n2y7b25ecuf">
                  {aboutFacts.map(f => <div key={f.label} data-id="element-619" data-mp="2ck79ugydapwz">
                      <dt className="text-[13px] text-muted" data-id="element-620" data-mp="2cj61wlcnejjh">{f.label}</dt>
                      <dd className="mt-1 text-[15px] leading-snug text-ink" data-id="element-621" data-mp="2glarn8zz6w0u">{f.value}</dd>
                    </div>)}
                </dl>
              </div>
              <figure className="order-first mx-auto w-full max-w-[360px] lg:order-none" data-id="element-622" data-mp="2lij7v41kcpzg">
                <div className="overflow-hidden rounded-[14px] bg-canvas p-2 shadow-[0_0_0_1px_rgba(70,28,64,0.07),0_18px_40px_-20px_rgba(70,28,64,0.25)]" data-id="element-623" data-mp="ma5rieo3l1q9">
                  <img src={PORTRAIT_URL} alt="Porträt von Konrad Walz" className="aspect-[4/5] w-full rounded-[10px] object-cover object-[50%_30%]" data-id="element-624" data-mp="joeqa9lsbe85" />
                </div>
                <figcaption className="mt-3 text-center text-[13px] text-muted" data-id="element-625" data-mp="9v7i6gzntk4d">Neuler · Ostalbkreis</figcaption>
              </figure>
            </section>

            {/* Timeline */}
            <section aria-labelledby="werdegang" className="mt-20 border-t border-line pt-14 md:mt-28 md:pt-20" data-id="element-626" data-mp="12k4um3h6wmp7">
              <h2 id="werdegang" className="font-display text-[30px] font-semibold leading-[1.08] tracking-[-0.035em] md:text-[40px]" data-id="element-627" data-mp="so1ji84d1dq4">
                Werdegang.
              </h2>
              <ol className="mt-10 max-w-[860px]" data-id="element-628" data-mp="3deiubx2koz9m">
                {aboutTimeline.map((step, i) => <li key={step.title} className="relative grid gap-1 pb-9 pl-8 last:pb-0 md:grid-cols-[150px_minmax(0,1fr)] md:gap-8 md:pl-10" data-id="element-629" data-mp="39cfeyxcs9aj2" data-mpi={i}>
                    <span aria-hidden="true" className={`absolute left-[5px] top-[7px] h-[9px] w-[9px] rounded-full ${i === aboutTimeline.length - 1 ? 'bg-ink' : 'border border-ink/40 bg-white'}`} data-id="element-630" data-mp="15vr09osuog8a" />
                    {i < aboutTimeline.length - 1 && <span aria-hidden="true" className="absolute bottom-0 left-[9px] top-[20px] w-px bg-line" data-id="element-631" data-mp="1otbe80u2tue4" />}
                    <p className="text-[14px] text-muted md:pt-[1px]" data-id="element-632" data-mp="1asqwz93t86mq">{step.period}</p>
                    <div data-id="element-633" data-mp="1eum5axk5rka0">
                      <h3 className="text-[17px] font-medium text-ink" data-id="element-634" data-mp="1etsvfnpwkenn">{step.title}</h3>
                      <p className="mt-1 text-[16px] leading-relaxed text-ink/70" data-id="element-635" data-mp="3vj84op6q7c20">{step.text}</p>
                    </div>
                  </li>)}
              </ol>
            </section>

            <figure className="mt-20 overflow-hidden rounded-[14px] bg-canvas shadow-[0_0_0_1px_rgba(70,28,64,0.07),0_18px_40px_-20px_rgba(70,28,64,0.25)] md:mt-28" data-id="element-636" data-mp="3s0shnkw0kk3q">
              <img src={TEAM_IMAGE_URL} alt="Konrad Walz im Gespräch mit zwei Begleitern vor herbstlichen Bäumen" loading="lazy" className="aspect-[16/9] w-full object-cover md:aspect-[21/9]" data-id="element-637" data-mp="5vf66mecffnx" />
            </figure>

            {/* Why "wir" */}
            <section aria-labelledby="warum-wir" className="mt-14 grid gap-8 md:mt-20 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] lg:gap-20" data-id="element-638" data-mp="34dkdj6abnh3a">
              <h2 id="warum-wir" className="font-display text-[30px] font-semibold leading-[1.08] tracking-[-0.035em] md:text-[40px]" data-id="element-639" data-mp="3eafavluuzbfz">
                Warum NMW Studio – und warum „wir“?
              </h2>
              <div className="space-y-5 text-[17px] leading-relaxed text-ink/75" data-id="element-640" data-mp="2ljxa4rhlxm5q">
                {aboutWhy.map(p => <p key={p} data-id="element-641" data-mp="3fbilij3zqyv5">{p}</p>)}
              </div>
            </section>

            {/* Video */}
            <section aria-labelledby="video" className="mt-20 border-t border-line pt-14 md:mt-28 md:pt-20" data-id="element-642" data-mp="3k27anrs77jr4">
              <div className="mx-auto max-w-[960px]" data-id="element-643" data-mp="3p0in8a9vo8d1">
                <h2 id="video" className="text-center font-display text-[30px] font-semibold leading-[1.08] tracking-[-0.035em] md:text-[40px]" data-id="element-644" data-mp="2sbayktdypj4w">
                  Im Video.
                </h2>
                <p className="mx-auto mt-4 max-w-[520px] text-center text-[17px] leading-relaxed text-ink/70" data-id="element-645" data-mp="31ay4nshmzd4a">
                  Ein Einblick hinter die Kulissen von NMW Studio.
                </p>
                <div className="mt-10" data-id="element-646" data-mp="gkp7hxndf0x1">
                  <VideoEmbed videoId="KgCpPr2OYdQ" title="Konrad Walz · NMW Studio" data-id="element-647" data-mp="gnuy5hbneauv" />
                </div>
              </div>
            </section>

            {/* CTA */}
            <div className="mt-20 flex flex-col gap-5 rounded-[12px] bg-graphite p-6 text-white md:mt-28 md:flex-row md:items-center md:justify-between md:p-8" data-id="element-648" data-mp="262cloy9w54ce">
              <div className="max-w-[560px]" data-id="element-649" data-mp="1wcklnoj0vj0j">
                <p className="text-[19px] font-medium tracking-[-0.01em]" data-id="element-650" data-mp="1wb9m60wm0xf8">Lassen Sie uns über Ihr Projekt sprechen.</p>
                <p className="mt-2 text-[15px] leading-relaxed text-white/60" data-id="element-651" data-mp="1rc5b14q4kpm9">
                  Skizze, Foto oder STEP-Datei genügen für eine erste Einschätzung.
                </p>
              </div>
              <a href="/#contact" className="inline-flex shrink-0 items-center justify-center rounded-[8px] bg-white px-5 py-3 text-[15px] font-medium text-ink transition-colors duration-150 hover:bg-white/85" data-id="element-652" data-mp="1ifnybx244joe">
                Kostenloses Angebot erhalten
              </a>
            </div>
            </div>
          </div>
        </Frame>
      </main>
      <Footer data-id="element-653" data-mp="32rbwol1v0k8a" />
    </div>;
}